/**
 * FERP Portal - Scope Checking Hook
 *
 * Provides `useHasScope` for checking if the current user has specific scopes.
 * Scopes are resolved from the user's roles via the backend GraphQL query.
 *
 * Usage:
 *   const { hasScope, loading } = useScopeCheck();
 *   if (hasScope('facility:admin')) { ... }
 *
 * Or for simple boolean check:
 *   const canEdit = useHasScope('task:write');
 */

import { useMemo } from 'react';
import { useGetAllowedNavigationQuery, ViewLayer } from '@/generated/graphql';
import { useTenantId, useViewLayer } from './AuthContext';

/**
 * Hook that provides scope checking capabilities.
 * Uses allowedNavigation query which resolves scopes from JWT auth context.
 */
export function useScopeCheck(): {
  hasScope: (scope: string) => boolean;
  hasScopeAny: (scopes: string[]) => boolean;
  hasScopeAll: (scopes: string[]) => boolean;
  scopes: string[];
  loading: boolean;
} {
  const tenantId = useTenantId();
  const viewLayer = useViewLayer();

  const getLayer = (): ViewLayer => {
    if (viewLayer === 'architect') return ViewLayer.ARCHITECT;
    if (viewLayer === 'admin') return ViewLayer.ADMIN;
    return ViewLayer.USER;
  };
  const layer = getLayer();

  const { data, loading } = useGetAllowedNavigationQuery({
    variables: { tenantId: tenantId ?? '', layer },
    skip: !tenantId,
    fetchPolicy: 'cache-first',
  });

  const scopes = useMemo(() => data?.allowedNavigation?.userScopes ?? [], [data]);
  const scopeSet = useMemo(() => new Set(scopes), [scopes]);

  const hasScope = useMemo(
    () => (scope: string) => scopeSet.has(scope),
    [scopeSet]
  );

  const hasScopeAny = useMemo(
    () => (checkScopes: string[]) => checkScopes.some((s) => scopeSet.has(s)),
    [scopeSet]
  );

  const hasScopeAll = useMemo(
    () => (checkScopes: string[]) => checkScopes.every((s) => scopeSet.has(s)),
    [scopeSet]
  );

  return { hasScope, hasScopeAny, hasScopeAll, scopes, loading };
}

/**
 * Simple hook for checking a single scope.
 * Returns false while loading (safe default).
 */
export function useHasScope(scope: string): boolean {
  const { hasScope, loading } = useScopeCheck();
  // Return false while loading to prevent flash of unauthorized content
  return !loading && hasScope(scope);
}

/**
 * Hook for checking if user has ANY of the provided scopes.
 */
export function useHasScopeAny(scopes: string[]): boolean {
  const { hasScopeAny, loading } = useScopeCheck();
  return !loading && hasScopeAny(scopes);
}

/**
 * Hook for checking if user has ALL of the provided scopes.
 */
export function useHasScopeAll(scopes: string[]): boolean {
  const { hasScopeAll, loading } = useScopeCheck();
  return !loading && hasScopeAll(scopes);
}
